import Peer, { DataConnection } from 'peerjs';

interface PlayerState {
  id: string;
  position: { x: number; y: number; z: number };
  rotation: number;
  health: number;
  action: string;
}

interface GameMessage {
  type: 'join' | 'leave' | 'state' | 'attack' | 'damage' | 'wave' | 'start' | 'anti-cheat';
  data?: PlayerState | PlayerState[] | { id: string; reason?: string };
}

export class NetworkManager {
  private peer: Peer | null = null;
  private connections: Map<string, DataConnection> = new Map();
  private isHost = false;
  private roomCode = '';
  
  
  async createRoom(): Promise<string> {
    this.isHost = true;
    this.roomCode = this.generateCode();
    
    return new Promise((resolve, reject) => {
      this.peer = new Peer(this.roomCode, {
        debug: 0
      });
      
      this.peer.on('open', () => {
        this.setupHostListeners();
        resolve(this.roomCode);
      });
      
      this.peer.on('error', (err) => {
        console.error('Peer error:', err);
        reject(err);
      });
    });
  }
  
  async joinRoom(code: string): Promise<void> {
    this.isHost = false;
    this.roomCode = code;
    
    return new Promise((resolve, reject) => {
      this.peer = new Peer({
        debug: 0
      });
      
      this.peer.on('open', () => {
        const conn = this.peer!.connect(code);
        
        conn.on('open', () => {
          this.connections.set('host', conn);
          this.setupClientListeners(conn);
          this.send({ type: 'join', data: this.createState() });
          resolve();
        });
        
        conn.on('error', reject);
      });
      
      this.peer.on('error', reject);
    });
  }
  
  private generateCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars[Math.floor(Math.random() * chars.length)];
    }
    return code;
  }
  
  private setupHostListeners(): void {
    if (!this.peer) return;
    
    this.peer.on('connection', (conn) => {
      conn.on('open', () => {
        this.connections.set(conn.peer, conn);
        this.setupClientListeners(conn);
        this.broadcast({
          type: 'join',
          data: Array.from(this.connections.keys()).map(id => ({
            id,
            position: { x: 0, y: 0, z: 0 },
            rotation: 0,
            health: 100,
            action: 'idle'
          }))
        });
      });
      
      conn.on('data', (data) => {
        this.validateAndProcess(conn, data as GameMessage);
      });
      
      conn.on('close', () => {
        this.connections.delete(conn.peer);
        this.broadcast({ type: 'leave', data: { id: conn.peer, reason: 'disconnect' } });
      });
    });
  }
  
  private setupClientListeners(conn: DataConnection): void {
    conn.on('data', (data) => {
      const msg = data as GameMessage;
      if (msg.type === 'anti-cheat') {
        const reason = (msg.data as { reason: string }).reason;
        console.warn('Anti-cheat violation:', reason);
      }
    });
  }
  
  private validateAndProcess(conn: DataConnection, msg: GameMessage): void {
    if (msg.type === 'state' && msg.data) {
      const state = msg.data as PlayerState;
      
      const lastPos = this.getLastPosition(state.id);
      const dx = Math.abs(state.position.x - (lastPos?.x || 0));
      const dz = Math.abs(state.position.z - (lastPos?.z || 0));
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > 20) {
        this.sendTo(conn, {
          type: 'anti-cheat',
          data: { id: state.id, reason: 'teleport detected' }
        });
        return;
      }
    }
    
    if (msg.type === 'join') {
      this.broadcast({ type: 'join', data: { id: conn.peer } });
    } else {
      this.broadcast(msg);
    }
  }
  
  private lastPositions: Map<string, { x: number; z: number; time: number }> = new Map();
  
  private getLastPosition(id: string): { x: number; z: number; time: number } | undefined {
    return this.lastPositions.get(id);
  }
  
  private createState(): PlayerState {
    return {
      id: this.peer?.id || 'local',
      position: { x: 0, y: 0, z: 0 },
      rotation: 0,
      health: 100,
      action: 'idle'
    };
  }
  
  send(msg: GameMessage): void {
    if (this.isHost) {
      this.broadcast(msg);
    } else {
      const hostConn = this.connections.get('host');
      if (hostConn) {
        hostConn.send(msg);
      }
    }
  }
  
  private sendTo(conn: DataConnection, msg: GameMessage): void {
    conn.send(msg);
  }
  
  private broadcast(msg: GameMessage): void {
    this.connections.forEach((conn) => {
      if (conn.open) {
        conn.send(msg);
      }
    });
  }
  
  cleanup(): void {
    if (this.peer) {
      this.peer.destroy();
      this.peer = null;
    }
    this.connections.clear();
  }
}